import * as React from "react";
import {
    DataGridPro,
    GridColumns,
    GridRenderCellParams,
    GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CclCommonLink from "../../../../components/common/cclCommonLink";
import { addCustomDataGridFilters } from "../../../../components/common/customDataGridFilters/customDataGridFilters";
import { ParticipantSearchResult } from "../../../../services/types/enterpriseParticipantApiTypes";
import CclDataGridToolbar from "../../../../components/common/cclDataGridToolbar/cclDataGridToolbar";

const COLUMNS = [
    {
        field: "esiKey",
        headerName: "Registration ID",
        renderCell: (params: GridRenderCellParams) => (
            <CclCommonLink
                text={params.value}
                keyVal={params.value}
                to={`/registration/${params.row.esiKey}`}
            />
        ),
        width: 125,
    },
    {
        field: "esKey",
        headerName: "Session ID",
        type: "string",
        width: 100,
    },
    {
        field: "projectCode",
        headerName: "Project ID",
        type: "string",
        width: 125,
    },
    {
        field: "sessionTitle",
        headerName: "Title",
        type: "string",
        width: 300,
        renderCell: (params: GridRenderCellParams) => (
            <CclCommonLink
                text={params.value}
                keyVal={params.value}
                to={`/session/${params.row.esKey}`}
            />
        ),
    },
    {
        field: "sessionType",
        headerName: "Type",
        type: "string",
        width: 175,
    },
    {
        field: "registrationStatus",
        headerName: "Status",
        width: 150,
        renderCell: (params: GridRenderCellParams) =>
            params.value === "Cancelled" ? (
                <>
                    Cancelled
                    <ErrorOutlineIcon color="error" fontSize="small" sx={{ pl: 1 }} />
                </>
            ) : (
                `${params.value}`
            ),
    },
    {
        field: "registrationDate",
        headerName: "Registered",
        valueGetter: (params: any) =>
            params.row.registrationDate != null
                ? params.row.registrationDate.toString().substring(0, 10)
                : null,
        renderCell: (params: GridRenderCellParams) => {
            if (params.value == null) return <span></span>;
            const [year, month, day] = (params.value as string).split("-");
            if (year === "9999") return <span></span>;
            return <span>{`${month}/${day}/${year}`}</span>;
        },
        width: 125,
    },
    {
        field: "startDate",
        headerName: "Start",
        valueGetter: (params: any) =>
            params.row.startDate != null
                ? params.row.startDate.toString().substring(0, 10)
                : null,
        renderCell: (params: GridRenderCellParams) => {
            if (params.value == null) return <span></span>;
            const [year, month, day] = (params.value as string).split("-");
            if (year === "9999") return <span></span>;
            return <span>{`${month}/${day}/${year}`}</span>;
        },
        width: 125,
    },
    {
        field: "sessionHostPlatform",
        headerName: "Hosting Platform",
        renderCell: (params: GridRenderCellParams) => <span>{params.row.sessionHostPlatform}</span>,
        width: 200,
    },
    {
        field: "sessionAssessmentPlatform",
        headerName: "Assessment Platform",
        renderCell: (params: GridRenderCellParams) => (
            <span>{params.row.sessionAssessmentPlatform}</span>
        ),
        width: 200,
    },
    {
        field: "sessionHasCompassSku",
        headerName: "Compass",
        valueGetter: (params: GridValueGetterParams) =>
            `${params.row.sessionHasCompassSku ? "Yes" : "No"}`,
        width: 85,
    },
];

export interface UserRegistrationsDataGridProps {
    registrations: ParticipantSearchResult[] | undefined;
    loading: boolean;
}

export const UserRegistrationsDataGridPro: React.FC<UserRegistrationsDataGridProps> = (props) => {
    return (
        <DataGridPro
            getRowId={(row) => row.esiKey}
            rows={props.registrations ?? []}
            columns={addCustomDataGridFilters(COLUMNS as GridColumns<any>)}
            loading={props.loading}
            components={{ Toolbar: CclDataGridToolbar }}
            componentsProps={{
                toolbar: {
                    showSaveState: false,
                    hideSettingsButtons: true,
                },
            }}
            initialState={{ sorting: { sortModel: [{ field: "startDate", sort: "desc" }] } }}
        />
    );
};

export default UserRegistrationsDataGridPro;
